import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import scrollIntoView from 'scroll-into-view';
import { getOrdinal, isStillPlaying, opponentName } from '../../functions';
import { IChatMessage } from '../../Models/LobbyModels';
import { IVisibleOpponent } from '../../Models/StateModels';
import Avatar from '../General/Avatar/Avatar';
import Ephemeral from '../General/Ephemeral/Ephemeral';
import OpponentTableCards from './OpponentTableCards';
import './Opponent.css';

export interface IOpponentProps {
  opponent: IVisibleOpponent;
  isNext: boolean;
  lastSentMessage: IChatMessage | undefined;
}

const PILL_CLASS_NAME =
  'absolute -top-[9px] right-[10px] rounded-full px-[8px] py-[2px] text-[11px] font-bold whitespace-nowrap';

function statusPill(opponent: IVisibleOpponent, isNext: boolean) {
  if (opponent.forfeited) {
    return {
      label: 'Left',
      className: 'bg-surface text-ink-muted ring-1 ring-hairline',
    };
  }
  if (opponent.place != null) {
    return {
      label: `Out · ${opponent.place}${getOrdinal(opponent.place)}`,
      className: 'bg-surface text-ink-muted ring-1 ring-hairline',
    };
  }
  if (opponent.isTurn) {
    return { label: 'Playing', className: 'bg-accent-fill text-accent-on' };
  }
  if (isNext) {
    return { label: 'Next', className: 'bg-straw text-straw-ink' };
  }
  return undefined;
}

function HandIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="7" width="11" height="14" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2h-4" />
    </svg>
  );
}

export default function Opponent({
  opponent,
  isNext,
  lastSentMessage,
}: IOpponentProps) {
  const seatRef = useRef<HTMLLIElement>(null);
  const isOut = !isStillPlaying(opponent);
  const pill = statusPill(opponent, isNext);
  const handCount = opponent.handCards.length;
  const name = opponentName(opponent);

  useEffect(() => {
    if (opponent.isTurn && seatRef.current) {
      scrollIntoView(seatRef.current, { time: 200, align: { lockY: true } });
    }
  }, [opponent.isTurn]);

  return (
    <li
      ref={seatRef}
      className={classNames(
        'relative flex w-[148px] shrink-0 flex-col gap-[8px] rounded-[14px] bg-surface p-[10px] text-ink shadow-soft transition-[box-shadow,opacity] duration-150',
        opponent.isTurn ? 'ring-2 ring-accent-ring' : 'ring-1 ring-hairline',
        { 'opacity-70': isOut }
      )}
    >
      {pill && (
        <span className={classNames(PILL_CLASS_NAME, pill.className)}>
          {pill.label}
        </span>
      )}
      <Ephemeral
        id={lastSentMessage}
        message={lastSentMessage?.message}
        classNames="seat-last-message"
      />
      <div className="flex items-center gap-[8px]">
        <Avatar
          name={name}
          colorIndex={opponent.playerIndex}
          className="size-[24px] text-[12px]"
        />
        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold">
          {name}
        </span>
      </div>
      {isOut ? (
        <span className="flex h-[38px] items-center justify-center text-[12px] font-medium text-ink-muted">
          {opponent.forfeited ? 'Left the game' : 'No cards left'}
        </span>
      ) : (
        <div className="flex h-[38px] items-center justify-between">
          <span className="flex items-center gap-[4px] text-[13px] font-semibold text-ink-muted">
            <HandIcon />
            {handCount}
            <span className="sr-only">
              {handCount === 1 ? ' card in hand' : ' cards in hand'}
            </span>
          </span>
          <OpponentTableCards
            visibleCards={opponent.visibleCards}
            hiddenCards={opponent.hiddenCards}
          />
        </div>
      )}
    </li>
  );
}
